import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingButton from "../LoadingButton/LoadingButton";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import PermissionsContent from "../PermissionsContent/PermissionsContent";
import "./SettingsContent.css";

const SettingsContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [calendarValue, setCalendarValue] = useState(new Date());
  const [selectedDateLeaveUsers, setSelectedDateLeaveUsers] = useState([]);
  const [selectedDateWorkingUsers, setSelectedDateWorkingUsers] = useState([]);
  const [leaveUsers, setLeaveUsers] = useState([]);
  const [workingUsers, setWorkingUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("calendar");
  const [formLoading, setFormLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scheduleType, setScheduleType] = useState("STANDARD");
  const [workHours, setWorkHours] = useState({ start: "09:00", end: "18:00" });
  const [requiredHours, setRequiredHours] = useState(8);
  const [shifts, setShifts] = useState({
    first: { start: "08:00", end: "16:00" },
    second: { start: "16:00", end: "00:00" },
  });
  const [workDays, setWorkDays] = useState([
    true,
    true,
    true,
    true,
    true,
    false,
    false,
  ]);

  useEffect(() => {
    setLoading(true);
    fetchUsers();
    fetchTodayLeaves();
    fetchTodayWorking();
    fetchSelectedDateData(calendarValue);
    if (location.state?.userId) {
      setSelectedUser(location.state.userId);
      if (location.state.userId !== "all") {
        fetchUserScheduleSettings(location.state.userId);
      }
    }
    if (location.state?.workSchedule) {
      setActiveTab("workSchedule");
    }
    if (location.state?.activeTab === "permissions") {
      setActiveTab("permissions");
    }
    setLoading(false);
  }, [location]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("user_id, name, surname")
        .eq("is_active", true)
        .order("name");
      if (!error && data) {
        setUsers(data);
      }
    } catch (error) {
      console.error("Kullanıcılar yüklenemedi:", error.message);
    }
  };

  const fetchTodayLeaves = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("permissions")
        .select(
          `
          permission_id,
          user_id,
          start_date,
          end_date,
          reason,
          status,
          users(name, surname)
        `
        )
        .lte("start_date", today)
        .gte("end_date", today)
        .eq("status", "approved");
      if (!error && data) {
        setLeaveUsers(data);
      }
    } catch (error) {
      console.error("İzinli personeller yüklenemedi:", error.message);
    }
  };

  const fetchTodayWorking = async () => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("user_id, name, surname")
        .eq("is_active", true);
      if (!error && data) {
        const leaveUserIds = leaveUsers.map((leave) => leave.user_id);
        const working = data.filter(
          (user) => !leaveUserIds.includes(user.user_id)
        );
        setWorkingUsers(working);
      }
    } catch (error) {
      console.error("Çalışan personeller yüklenemedi:", error.message);
    }
  };

  const fetchUserScheduleSettings = async (userId) => {
    if (!userId || userId === "all") return;
    try {
      // Önce work_schedules tablosundan kontrol et
      const { data: workSchedules, error: workSchedulesError } = await supabase
        .from("work_schedules")
        .select("*")
        .eq("user_id", userId)
        .order("date", { ascending: false })
        .limit(1);

      if (!workSchedulesError && workSchedules && workSchedules.length > 0) {
        const schedule = workSchedules[0];
        setScheduleType("CUSTOM");
        setWorkHours({
          start: schedule.start_time.substring(0, 5),
          end: schedule.end_time.substring(0, 5),
        });

        // days dizisini workDays boolean dizisine dönüştür
        const newWorkDays = [false, false, false, false, false, false, false];
        if (schedule.days && Array.isArray(schedule.days)) {
          const dayIndexMap = {
            Mon: 0,
            Tue: 1,
            Wed: 2,
            Thu: 3,
            Fri: 4,
            Sat: 5,
            Sun: 6,
          };
          schedule.days.forEach((day) => {
            const index = dayIndexMap[day];
            if (index !== undefined) {
              newWorkDays[index] = true;
            }
          });
        }
        setWorkDays(newWorkDays);
        return;
      }

      // Eğer work_schedules tablosunda kayıt yoksa, users tablosundan schedule_settings alanını kontrol et
      const { data, error } = await supabase
        .from("users")
        .select("schedule_settings")
        .eq("user_id", userId)
        .single();
      if (!error && data && data.schedule_settings) {
        const settings = data.schedule_settings;
        setScheduleType(settings.type || "STANDARD");
        if (settings.workHours) {
          setWorkHours(settings.workHours);
        }
        if (settings.requiredHours) {
          setRequiredHours(settings.requiredHours);
        }
        if (settings.shifts) {
          setShifts(settings.shifts);
        }
        if (settings.workDays) {
          setWorkDays(settings.workDays);
        }
      }
    } catch (error) {
      console.error("Mesai ayarları yüklenemedi:", error.message);
    }
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    if (userId && userId !== "all") {
      fetchUserScheduleSettings(userId);
    } else {
      setScheduleType("STANDARD");
      setWorkHours({ start: "09:00", end: "18:00" });
      setRequiredHours(8);
      setShifts({
        first: { start: "08:00", end: "16:00" },
        second: { start: "16:00", end: "00:00" },
      });
      setWorkDays([true, true, true, true, true, false, false]);
    }
  };

  const saveScheduleSettings = async () => {
    if (!selectedUser) {
      setNotification({
        show: true,
        message: "Lütfen bir personel seçin",
        type: "error",
      });
      return;
    }
    setFormLoading(true);
    try {
      // workDays boolean dizisini gün adları dizisine dönüştür
      const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const selectedDays = workDays
        .map((isActive, index) => (isActive ? dayNames[index] : null))
        .filter(Boolean);

      if (selectedUser === "all") {
        // Tüm personeller için ayarları güncelle
        const scheduleSettings = {
          type: scheduleType,
          workHours: scheduleType === "STANDARD" ? workHours : undefined,
          requiredHours:
            scheduleType === "FLEXIBLE" ? requiredHours : undefined,
          shifts: scheduleType === "SHIFT" ? shifts : undefined,
          workDays: workDays,
          updated_at: new Date().toISOString(),
        };

        // Önce users tablosunu güncelle
        const { data: usersUpdate, error: usersError } = await supabase
          .from("users")
          .update({ schedule_settings: scheduleSettings })
          .eq("is_active", true);

        if (usersError) {
          console.error("Kullanıcılar güncellenemedi:", usersError);
        }

        // Sonra work_schedules tablosunu güncelle
        if (scheduleType === "STANDARD") {
          // Önce mevcut kayıtları sil
          const { data: activeUsers, error: fetchError } = await supabase
            .from("users")
            .select("user_id")
            .eq("is_active", true);

          if (!fetchError && activeUsers) {
            await supabase
              .from("work_schedules")
              .delete()
              .in(
                "user_id",
                activeUsers.map((u) => u.user_id)
              );

            // Yeni kayıtları ekle
            const today = new Date();
            const schedulesToInsert = [];
            for (let i = 0; i < 30; i++) {
              const date = new Date(today);
              date.setDate(today.getDate() + i);
              const dayIndex = date.getDay();
              const adjustedDayIndex = dayIndex === 0 ? 6 : dayIndex - 1;

              if (workDays[adjustedDayIndex]) {
                activeUsers.forEach((user) => {
                  schedulesToInsert.push({
                    user_id: user.user_id,
                    date: date.toISOString().split("T")[0],
                    start_time: workHours.start,
                    end_time: workHours.end,
                    days: selectedDays, // Dönüştürülmüş günler
                  });
                });
              }
            }

            if (schedulesToInsert.length > 0) {
              await supabase.from("work_schedules").insert(schedulesToInsert);
            }
          }
        }

        setNotification({
          show: true,
          message: "Tüm personeller için mesai ayarları başarıyla kaydedildi",
          type: "success",
        });

        window.dispatchEvent(
          new CustomEvent("workScheduleUpdated", {
            detail: { userId: "all", allUsers: true },
          })
        );
      } else {
        // Tek bir kullanıcı için ayarları güncelle
        const scheduleSettings = {
          type: scheduleType,
          workHours: scheduleType === "STANDARD" ? workHours : undefined,
          requiredHours:
            scheduleType === "FLEXIBLE" ? requiredHours : undefined,
          shifts: scheduleType === "SHIFT" ? shifts : undefined,
          workDays: workDays,
          updated_at: new Date().toISOString(),
        };

        // Önce users tablosunu güncelle
        const { data: userUpdate, error: userError } = await supabase
          .from("users")
          .update({ schedule_settings: scheduleSettings })
          .eq("user_id", selectedUser);

        if (userError) {
          console.error("Kullanıcı güncellenemedi:", userError);
        }

        // Sonra work_schedules tablosunu güncelle
        if (scheduleType === "STANDARD") {
          // Önce mevcut kayıtları sil
          await supabase
            .from("work_schedules")
            .delete()
            .eq("user_id", selectedUser);

          // Yeni kayıtları ekle
          const today = new Date();
          const schedulesToInsert = [];
          for (let i = 0; i < 30; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            const dayIndex = date.getDay();
            const adjustedDayIndex = dayIndex === 0 ? 6 : dayIndex - 1;

            if (workDays[adjustedDayIndex]) {
              schedulesToInsert.push({
                user_id: selectedUser,
                date: date.toISOString().split("T")[0],
                start_time: workHours.start,
                end_time: workHours.end,
                days: selectedDays, // Dönüştürülmüş günler
              });
            }
          }

          if (schedulesToInsert.length > 0) {
            await supabase.from("work_schedules").insert(schedulesToInsert);
          }
        }

        setNotification({
          show: true,
          message: "Mesai ayarları başarıyla kaydedildi",
          type: "success",
        });

        window.dispatchEvent(
          new CustomEvent("workScheduleUpdated", {
            detail: { userId: selectedUser },
          })
        );
      }
    } catch (error) {
      console.error("Mesai ayarları kaydedilemedi:", error);
      setNotification({
        show: true,
        message: "Mesai ayarları kaydedilemedi: " + error.message,
        type: "error",
      });
    } finally {
      setFormLoading(false);
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
    }
  };

  const handleWorkDayChange = (index) => {
    const newWorkDays = [...workDays];
    newWorkDays[index] = !newWorkDays[index];
    setWorkDays(newWorkDays);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDay = (date) => {
    return new Date(date).toLocaleDateString("tr-TR", {
      weekday: "long",
    });
  };

  const getDayStatus = (date) => {
    const today = new Date();
    const dateObj = new Date(date);
    if (dateObj.toDateString() === today.toDateString()) {
      return "today";
    }
    const day = dateObj.getDay();
    if (day === 0 || day === 6) {
      return "weekend";
    }
    return "weekday";
  };

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const status = getDayStatus(date);
      return status;
    }
    return null;
  };

  const handleCalendarChange = (date) => {
    setCalendarValue(date);
    fetchSelectedDateData(date);
  };

  const fetchSelectedDateData = async (date) => {
    const dateString = date.toISOString().split("T")[0];
    try {
      const { data: leaveData, error: leaveError } = await supabase
        .from("permissions")
        .select(
          `
          permission_id,
          user_id,
          start_date,
          end_date,
          reason,
          status,
          users(name, surname)
        `
        )
        .lte("start_date", dateString)
        .gte("end_date", dateString)
        .eq("status", "approved");
      if (!leaveError && leaveData) {
        setSelectedDateLeaveUsers(leaveData);
      } else {
        setSelectedDateLeaveUsers([]);
      }
      const { data: allUsers, error: usersError } = await supabase
        .from("users")
        .select("user_id, name, surname")
        .eq("is_active", true);
      if (!usersError && allUsers) {
        const leaveUserIds = leaveData
          ? leaveData.map((leave) => leave.user_id)
          : [];
        const working = allUsers.filter(
          (user) => !leaveUserIds.includes(user.user_id)
        );
        setSelectedDateWorkingUsers(working);
      } else {
        setSelectedDateWorkingUsers([]);
      }
    } catch (error) {
      console.error("Seçili tarih verileri yüklenemedi:", error.message);
      setSelectedDateLeaveUsers([]);
      setSelectedDateWorkingUsers([]);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p className="loading-text">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="settings-content">
      <div className="settings-list">
        <div className="list-header">
          <div className="header-content">
            <h2>Ayarlar</h2>
            <div className="header-actions">
              <button
                className={`tab-btn ${
                  activeTab === "calendar" ? "active" : ""
                }`}
                onClick={() => setActiveTab("calendar")}
              >
                <i className="fas fa-calendar-alt"></i> Takvim
              </button>
              <button
                className={`tab-btn ${
                  activeTab === "workSchedule" ? "active" : ""
                }`}
                onClick={() => setActiveTab("workSchedule")}
              >
                <i className="fas fa-business-hours"></i> Mesai Ayarları
              </button>
              <button
                className={`tab-btn ${
                  activeTab === "permissions" ? "active" : ""
                }`}
                onClick={() => setActiveTab("permissions")}
              >
                <i className="fas fa-user-shield"></i> İzin Yönetimi
              </button>
            </div>
          </div>
        </div>
        {activeTab === "calendar" && (
          <>
            <div className="settings-container">
              {notification.show && (
                <div className={`notification ${notification.type}`}>
                  {notification.message}
                </div>
              )}
              <div className="calendar-permissions-container">
                <div className="calendar-section">
                  <h3>Takvim</h3>
                  <div className="calendar-wrapper">
                    <Calendar
                      onChange={handleCalendarChange}
                      value={calendarValue}
                      locale="tr-TR"
                      className="modern-calendar"
                      tileClassName={tileClassName}
                    />
                  </div>
                  <div className="selected-date">
                    <h4>Seçili Tarih</h4>
                    <p>{formatDate(calendarValue.toISOString())}</p>
                    <p>{formatDay(calendarValue.toISOString())}</p>
                  </div>
                </div>
                <div className="leave-section">
                  <h3>Seçili Tarihteki İzinli Personeller</h3>
                  <div className="leave-list">
                    {selectedDateLeaveUsers.length > 0 ? (
                      selectedDateLeaveUsers.map((leave) => (
                        <div key={leave.permission_id} className="leave-item">
                          <div className="leave-user">
                            <i className="fas fa-user-circle"></i>
                            <span>
                              {leave.users.name} {leave.users.surname}
                            </span>
                          </div>
                          <div className="leave-reason">
                            <i className="fas fa-sticky-note"></i>
                            <span>{leave.reason}</span>
                          </div>
                          <div className="leave-dates">
                            <i className="fas fa-calendar-day"></i>
                            <span>
                              {formatDate(leave.start_date)} -{" "}
                              {formatDate(leave.end_date)}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-leaves">
                        <i className="fas fa-calendar-check"></i>
                        <p>Seçili tarihte izinli personel bulunmuyor</p>
                      </div>
                    )}
                  </div>
                  <div className="working-section">
                    <h3>Seçili Tarihte Çalışan Personeller</h3>
                    <div className="working-list">
                      {selectedDateWorkingUsers.length > 0 ? (
                        selectedDateWorkingUsers.map((user) => (
                          <div key={user.user_id} className="working-item">
                            <div className="working-user">
                              <i className="fas fa-user-check"></i>
                              <span>
                                {user.name} {user.surname}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-working">
                          <i className="fas fa-users-slash"></i>
                          <p>Seçili tarihte çalışan personel bulunmuyor</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {activeTab === "workSchedule" && (
          <div className="work-schedule-tab">
            <div className="work-schedule-header">
              <h3>Mesai Ayarları</h3>
              <div className="header-actions">
                <div className="user-select">
                  <select
                    value={selectedUser}
                    onChange={handleUserChange}
                    className="filter-select"
                  >
                    <option value="all">Tüm Personeller</option>
                    {users.map((user) => (
                      <option key={user.user_id} value={user.user_id}>
                        {user.name} {user.surname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {notification.show && (
              <div className={`notification ${notification.type}`}>
                {notification.message}
              </div>
            )}
            {selectedUser ? (
              <div className="schedule-settings-container">
                <div className="schedule-form">
                  <h4>
                    {selectedUser === "all"
                      ? "Tüm Personeller İçin Mesai Planı Ayarları"
                      : "Mesai Planı Ayarları"}
                  </h4>
                  <div className="form-group">
                    <label>Mesai Tipi</label>
                    <select
                      value={scheduleType}
                      onChange={(e) => setScheduleType(e.target.value)}
                      className="form-control"
                    >
                      <option value="STANDARD">Standart</option>
                      <option value="FLEXIBLE">Esnek</option>
                      <option value="SHIFT">Vardiya</option>
                    </select>
                  </div>
                  {scheduleType === "STANDARD" && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Başlangıç Saati</label>
                        <input
                          type="time"
                          value={workHours.start}
                          onChange={(e) =>
                            setWorkHours({
                              ...workHours,
                              start: e.target.value,
                            })
                          }
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Bitiş Saati</label>
                        <input
                          type="time"
                          value={workHours.end}
                          onChange={(e) =>
                            setWorkHours({ ...workHours, end: e.target.value })
                          }
                          className="form-control"
                        />
                      </div>
                    </div>
                  )}
                  {scheduleType === "FLEXIBLE" && (
                    <div className="form-group">
                      <label>Gerekli Çalışma Saati (günlük)</label>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={requiredHours}
                        onChange={(e) =>
                          setRequiredHours(parseInt(e.target.value))
                        }
                        className="form-control"
                      />
                    </div>
                  )}
                  {scheduleType === "SHIFT" && (
                    <div className="shift-settings">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Vardiya 1 Başlangıç</label>
                          <input
                            type="time"
                            value={shifts.first.start}
                            onChange={(e) =>
                              setShifts({
                                ...shifts,
                                first: {
                                  ...shifts.first,
                                  start: e.target.value,
                                },
                              })
                            }
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Vardiya 1 Bitiş</label>
                          <input
                            type="time"
                            value={shifts.first.end}
                            onChange={(e) =>
                              setShifts({
                                ...shifts,
                                first: { ...shifts.first, end: e.target.value },
                              })
                            }
                            className="form-control"
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Vardiya 2 Başlangıç</label>
                          <input
                            type="time"
                            value={shifts.second.start}
                            onChange={(e) =>
                              setShifts({
                                ...shifts,
                                second: {
                                  ...shifts.second,
                                  start: e.target.value,
                                },
                              })
                            }
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Vardiya 2 Bitiş</label>
                          <input
                            type="time"
                            value={shifts.second.end}
                            onChange={(e) =>
                              setShifts({
                                ...shifts,
                                second: {
                                  ...shifts.second,
                                  end: e.target.value,
                                },
                              })
                            }
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Haftalık Çalışma Günü</label>
                    <div className="weekdays-selector">
                      {[
                        "Pazartesi",
                        "Salı",
                        "Çarşamba",
                        "Perşembe",
                        "Cuma",
                        "Cumartesi",
                        "Pazar",
                      ].map((day, index) => (
                        <label key={day} className="weekday-checkbox">
                          <input
                            type="checkbox"
                            checked={workDays[index]}
                            onChange={() => handleWorkDayChange(index)}
                          />
                          <span>{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-actions">
                    <LoadingButton
                      type="button"
                      className="tab-btn"
                      isLoading={formLoading}
                      onClick={saveScheduleSettings}
                    >
                      Kaydet
                    </LoadingButton>
                  </div>
                </div>
                <div className="schedule-preview">
                  <h4>Mesai Planı Önizleme</h4>
                  <div className="preview-card">
                    {scheduleType === "STANDARD" && (
                      <div className="standard-schedule">
                        <div className="schedule-type">Standart Mesai</div>
                        <div className="schedule-hours">
                          {workHours.start} - {workHours.end}
                        </div>
                        <div className="schedule-days">
                          {workDays.filter((day) => day).length} gün/hafta
                        </div>
                      </div>
                    )}
                    {scheduleType === "FLEXIBLE" && (
                      <div className="flexible-schedule">
                        <div className="schedule-type">Esnek Mesai</div>
                        <div className="schedule-hours">
                          Günde {requiredHours} saat
                        </div>
                        <div className="schedule-days">
                          {workDays.filter((day) => day).length} gün/hafta
                        </div>
                      </div>
                    )}
                    {scheduleType === "SHIFT" && (
                      <div className="shift-schedule">
                        <div className="schedule-type">Vardiya Sistemi</div>
                        <div className="shift-details">
                          <div>
                            Vardiya 1: {shifts.first.start} - {shifts.first.end}
                          </div>
                          <div>
                            Vardiya 2: {shifts.second.start} -{" "}
                            {shifts.second.end}
                          </div>
                        </div>
                        <div className="schedule-days">
                          {workDays.filter((day) => day).length} gün/hafta
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-user-selected">
                <i className="fas fa-user-clock"></i>
                <p>
                  Lütfen mesai ayarlarını düzenlemek için bir personel seçin
                </p>
              </div>
            )}
          </div>
        )}
        {activeTab === "permissions" && <PermissionsContent />}
      </div>
    </div>
  );
};

export default SettingsContent;
